/**
 * Keep the screen awake around the reminders.
 *
 * The spoken reminder runs in the page, and a phone that has dimmed and locked has stopped
 * running it — the push notification is what covers that case. So the lock is held only for
 * the short stretch where a reminder is actually due, never as a general policy: holding a
 * screen awake is the rudest thing a web app can do to a battery.
 */

import { useEffect } from "react";
import { noteAlertDiagnostics } from "./alertDiagnostics";

/**
 * Take or drop the lock by hand, from a tap.
 *
 * The automatic one asks on a timer, with no user activation behind it. Asking from a real
 * gesture is the only way to tell a platform that refuses outright from one that only wanted
 * to be asked at a better moment.
 */
let manualSentinel: WakeLockSentinel | null = null;

export async function toggleWakeLock(): Promise<string> {
	if (!("wakeLock" in navigator)) {
		return "not supported on this browser";
	}
	if (manualSentinel) {
		await manualSentinel.release().catch(() => undefined);
		manualSentinel = null;
		noteAlertDiagnostics({ wakeLock: "idle", wakeLockError: null });
		return "released";
	}
	try {
		manualSentinel = await navigator.wakeLock.request("screen");
		manualSentinel.addEventListener("release", () => {
			manualSentinel = null;
		});
		noteAlertDiagnostics({ wakeLock: "held", wakeLockError: null });
		return "held — the screen should stay lit";
	} catch (cause) {
		const error = cause instanceof Error ? `${cause.name}: ${cause.message}` : "refused";
		noteAlertDiagnostics({ wakeLock: "refused", wakeLockError: error });
		return error;
	}
}

export function useWakeLock(active: boolean): void {
	useEffect(() => {
		if (!active || !("wakeLock" in navigator)) {
			return;
		}

		let sentinel: WakeLockSentinel | null = null;
		let released = false;

		const acquire = async () => {
			if (released || document.visibilityState !== "visible" || sentinel) {
				return;
			}
			try {
				sentinel = await navigator.wakeLock.request("screen");
				noteAlertDiagnostics({ wakeLock: "held", wakeLockError: null });
				sentinel.addEventListener("release", () => {
					sentinel = null;
					noteAlertDiagnostics({ wakeLock: released ? "idle" : "refused" });
				});
			} catch (cause) {
				// Denied, or the tab lost visibility mid-request. The push alert still covers it.
				const error = cause instanceof Error ? `${cause.name}: ${cause.message}` : "refused";
				noteAlertDiagnostics({ wakeLock: "refused", wakeLockError: error });
			}
		};

		// A refusal often just means there was no user activation, so try again on the next touch.
		const retry = () => {
			void acquire();
		};

		// The lock is dropped whenever the page hides, so it has to be taken again on return.
		const onVisibility = () => {
			void acquire();
		};

		void acquire();
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("pointerdown", retry);

		return () => {
			released = true;
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("pointerdown", retry);
			void sentinel?.release().catch(() => undefined);
			sentinel = null;
			noteAlertDiagnostics({ wakeLock: "idle" });
		};
	}, [active]);
}
